/** DOM-Aufbau für das Flotten-Menü. Wizard-Flow mit 3 Schritten im "Manöver"-Tab. */
import { fontSm, fontXs, padMd, padSm, padXs, gapSm, radius, touchBtn } from './UITheme';
import { SHIP_NAMES } from './TravelMenuState';
import { SHIP_TYPE_DEFS } from '../config/ShipTypes';
import {
  div, lasche, wizardSection, selectEl, mapBtn, smallBtn, modeCard,
  CLR_PRIMARY, CLR_TEXT, CLR_DIM, BORDER_SUB, HOLO_BRD, HOLO_GLOW, SCAN, HOLO_BG,
} from './TravelMenuDOMHelpers';

export type { OwnPlanet } from './TravelMenuState';
export type FleetTabId = 'active' | 'inactive' | 'hub';
export { populatePlanetSelect, setLascheActive, setModeCardActive, updateStepIndicator } from './TravelMenuDOMHelpers';

export interface TravelMenuDOMResult {
  root: HTMLDivElement;
  tabBtns: Record<FleetTabId, HTMLButtonElement>;
  activeContent: HTMLDivElement;
  inactiveContent: HTMLDivElement;
  hubContent: HTMLDivElement;
  activeTravelsContainer: HTMLDivElement;
  inactiveTravelsContainer: HTMLDivElement;
  stepIndicator: HTMLDivElement;
  backBtn: HTMLButtonElement;
  nextBtn: HTMLButtonElement;
  step1El: HTMLDivElement;
  startSelect: HTMLSelectElement;
  startLabel: HTMLSpanElement;
  mapStartBtn: HTMLButtonElement;
  endSelect: HTMLSelectElement;
  endLabel: HTMLSpanElement;
  mapEndBtn: HTMLButtonElement;
  step2El: HTMLDivElement;
  sliders: HTMLInputElement[];
  sliderLabels: HTMLSpanElement[];
  shipWarpIcons: HTMLSpanElement[];
  warpInfoEl: HTMLDivElement;
  allBtn: HTMLButtonElement;
  noneBtn: HTMLButtonElement;
  step3El: HTMLDivElement;
  modeCards: HTMLDivElement[];
  sendBtn: HTMLButtonElement;
  errorEl: HTMLDivElement;
  statusEl: HTMLDivElement;
}

export function createTravelMenuDOM(onClose: () => void): TravelMenuDOMResult {
  const mobile = window.innerWidth < 768;
  const root = div({ position: 'fixed', zIndex: '15000', fontFamily: 'monospace', fontSize: fontSm(), color: CLR_TEXT, display: 'none', flexDirection: 'column', overflow: 'visible', pointerEvents: 'auto' });
  Object.assign(root.style, {
    top: '0', right: '0', bottom: '0', left: 'auto',
    width: mobile ? 'clamp(280px, 78vw, 400px)' : 'clamp(300px, 32vw, 420px)',
    borderRadius: '10px 0 0 10px', background: SCAN + ',' + HOLO_BG,
    border: 'none', borderLeft: HOLO_BRD,
    boxShadow: mobile ? HOLO_GLOW : HOLO_GLOW + ',0 0 32px rgba(0,0,0,0.3)',
  });

  // Tabs
  const tabsContainer = div({ position: 'absolute', top: 'clamp(12px, 2vw, 20px)', right: '100%', display: 'flex', flexDirection: 'column', gap: '3px' });
  const tabActive = lasche('Aktive', true);
  const tabInactive = lasche('Inaktiv', false);
  const tabHub = lasche('Man\u00F6ver', false);
  tabsContainer.append(tabActive, tabInactive, tabHub);
  root.appendChild(tabsContainer);

  // Header
  const header = div({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', marginBottom: '2px' });
  const title = document.createElement('span');
  title.textContent = '\u{1F680} Flotten';
  Object.assign(title.style, { fontSize: '15px', letterSpacing: '1px', color: '#bdf' });
  const closeBtn = document.createElement('span');
  closeBtn.textContent = '\u2715';
  Object.assign(closeBtn.style, { cursor: 'pointer', color: 'rgba(100,180,255,0.4)', fontSize: '18px', lineHeight: '1', padding: '4px 2px' });
  closeBtn.addEventListener('pointerdown', (e) => { e.stopPropagation(); onClose(); }); closeBtn.addEventListener('pointerenter', () => { closeBtn.style.color = CLR_PRIMARY; }); closeBtn.addEventListener('pointerleave', () => { closeBtn.style.color = 'rgba(100,180,255,0.4)'; });
  header.append(title, closeBtn);
  root.appendChild(header);

  const subtitle = div({ color: '#556', fontSize: '11px', padding: '0 14px 8px', borderBottom: '1px solid rgba(100,180,255,0.08)', marginBottom: '2px' });
  subtitle.textContent = 'Flotten verwalten \u00B7 Man\u00F6ver planen';
  root.appendChild(subtitle);

  // Active Content
  const activeContent = div({ flex: '1', display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden', padding: '8px 14px', gap: '6px', minHeight: '0' });
  const activeTravelsContainer = div({ display: 'flex', flexDirection: 'column', gap: gapSm() });
  const noTravels = document.createElement('span');
  noTravels.textContent = 'Keine aktiven Flotten';
  Object.assign(noTravels.style, { color: CLR_DIM, fontSize: fontXs(), padding: padSm() });
  activeTravelsContainer.appendChild(noTravels);
  activeContent.appendChild(activeTravelsContainer);

  // Inactive Content
  const inactiveContent = div({ flex: '1', display: 'none', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden', padding: '8px 14px', gap: '6px', minHeight: '0' });
  const inactiveTravelsContainer = div({ display: 'flex', flexDirection: 'column', gap: gapSm() });
  const noStationed = document.createElement('span');
  noStationed.textContent = 'Keine stationierten Flotten';
  Object.assign(noStationed.style, { color: CLR_DIM, fontSize: fontXs(), padding: padSm() });
  inactiveTravelsContainer.appendChild(noStationed);
  inactiveContent.appendChild(inactiveTravelsContainer);

  // Hub Content (Wizard)
  const hubContent = div({ flex: '1', display: 'none', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden', minHeight: '0' });
  const stepIndicator = _buildStepIndicator();
  hubContent.appendChild(stepIndicator);

  // Step 1
  const step1El = div({ display: 'flex', flexDirection: 'column', gap: '10px', padding: '8px 14px', flex: '1' });
  const startSection = wizardSection('START');
  const startSelect = selectEl('Startplanet w\u00E4hlen...');
  const startLabel = document.createElement('span');
  startLabel.style.display = 'none';
  Object.assign(startLabel.style, { fontSize: fontXs(), color: CLR_PRIMARY, padding: padXs() });
  const mapStartBtn = mapBtn('Feld auf Karte w\u00E4hlen');
  startSection.append(startSelect, startLabel, mapStartBtn);
  const endSection = wizardSection('ZIEL');
  const endSelect = selectEl('Zielplanet w\u00E4hlen...');
  const endLabel = document.createElement('span');
  endLabel.style.display = 'none';
  Object.assign(endLabel.style, { fontSize: fontXs(), color: CLR_PRIMARY, padding: padXs() });
  const mapEndBtn = mapBtn('Feld auf Karte w\u00E4hlen');
  endSection.append(endSelect, endLabel, mapEndBtn);
  step1El.append(startSection, endSection);

  // Step 2
  const step2El = div({ display: 'none', flexDirection: 'column', gap: '6px', padding: '8px 14px', flex: '1' });
  const step2Title = wizardSection('FLOTTE ZUSAMMENSTELLEN');
  const quickRow = div({ display: 'flex', gap: gapSm(), marginBottom: '4px' });
  const allBtn = smallBtn('Alle');
  const noneBtn = smallBtn('Keine');
  quickRow.append(allBtn, noneBtn);
  step2Title.appendChild(quickRow);
  const sliders: HTMLInputElement[] = [];
  const sliderLabels: HTMLSpanElement[] = [];
  const shipWarpIcons: HTMLSpanElement[] = [];
  for (let i = 0; i < 8; i++) {
    const row = div({ display: 'flex', alignItems: 'center', gap: gapSm(), padding: '2px 0' });
    const nameWrap = div({ display: 'flex', alignItems: 'center', gap: '3px', width: '90px', flexShrink: '0' });
    const name = document.createElement('span');
    name.textContent = SHIP_NAMES[i];
    Object.assign(name.style, { fontSize: fontXs(), color: CLR_PRIMARY });
    const warpIcon = document.createElement('span');
    if (SHIP_TYPE_DEFS[i].warpCapable) { warpIcon.textContent = '\u26A1'; warpIcon.title = 'Warp-f\u00E4hig'; warpIcon.style.fontSize = '10px'; }
    shipWarpIcons.push(warpIcon);
    nameWrap.append(name, warpIcon);
    const slider = document.createElement('input');
    slider.type = 'range'; slider.min = '0'; slider.max = '0'; slider.value = '0';
    Object.assign(slider.style, { flex: '1', accentColor: CLR_PRIMARY, cursor: 'pointer', height: '3px' });
    const label = document.createElement('span');
    label.textContent = '0/0';
    Object.assign(label.style, { minWidth: '40px', textAlign: 'right', fontSize: fontXs(), color: CLR_DIM, fontFamily: 'monospace' });
    sliders.push(slider); sliderLabels.push(label);
    row.append(nameWrap, slider, label);
    step2Title.appendChild(row);
  }
  step2El.appendChild(step2Title);
  const warpInfoEl = div({ display: 'none', flexDirection: 'column', gap: '3px', padding: '8px 10px', marginTop: '6px', background: 'rgba(100,180,255,0.04)', border: BORDER_SUB, borderRadius: radius() });
  step2El.appendChild(warpInfoEl);

  // Step 3
  const step3El = div({ display: 'none', flexDirection: 'column', gap: '8px', padding: '8px 14px', flex: '1' });
  const step3Title = wizardSection('MODUS W\u00C4HLEN');
  const modeCards: HTMLDivElement[] = [];
  for (const m of [{ n: 'Normal', d: 'Standard-Reise', i: '\u{1F6A2}', e: true }, { n: 'Warp', d: 'Schnelle Reise', i: '\u26A1', e: true }, { n: 'Aufkl\u00E4rung', d: 'Coming Soon', i: '\u{1F50D}', e: false }, { n: 'Eroberung', d: 'Coming Soon', i: '\u2694\uFE0F', e: false }]) {
    const c = modeCard(m.n, m.d, m.i, m.e); modeCards.push(c); step3Title.appendChild(c);
  }
  step3El.appendChild(step3Title);
  const errorEl = div({ color: '#f66', fontSize: fontXs(), minHeight: '14px', padding: padXs() });
  const statusEl = div({ color: CLR_DIM, fontSize: fontXs(), minHeight: '14px', padding: padXs() });
  const sendBtn = document.createElement('button');
  sendBtn.textContent = 'FLOTTE SENDEN';
  Object.assign(sendBtn.style, touchBtn({ fontSize: fontSm(), padding: `${padSm()} ${padMd()}`, borderRadius: radius(), border: '1px solid #2a7a4a', fontWeight: '700', letterSpacing: '1px', width: '100%', background: 'linear-gradient(180deg, #1a5a3a, #0a3a2a)', boxShadow: '0 0 10px rgba(50,200,120,0.12)', marginTop: padXs(), textAlign: 'center', fontFamily: 'monospace', color: '#8f8', cursor: 'pointer' }));
  sendBtn.addEventListener('pointerenter', () => { sendBtn.style.filter = 'brightness(1.15)'; }); sendBtn.addEventListener('pointerleave', () => { sendBtn.style.filter = ''; });
  step3El.append(sendBtn, errorEl, statusEl);

  // Nav
  const navRow = div({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px 12px', gap: '8px', borderTop: '1px solid rgba(100,180,255,0.08)', marginTop: 'auto' });
  const backBtn = document.createElement('button');
  backBtn.textContent = '\u2190 Zur\u00FCck';
  Object.assign(backBtn.style, { background: 'transparent', color: CLR_DIM, border: BORDER_SUB, fontFamily: 'monospace', cursor: 'pointer', fontSize: fontXs(), padding: '6px 14px', borderRadius: radius(), transition: 'color 150ms, border-color 150ms', visibility: 'hidden' });
  backBtn.addEventListener('pointerenter', () => { backBtn.style.color = CLR_PRIMARY; backBtn.style.borderColor = 'rgba(100,180,255,0.4)'; }); backBtn.addEventListener('pointerleave', () => { backBtn.style.color = CLR_DIM; backBtn.style.borderColor = 'rgba(100,180,255,0.12)'; });
  const nextBtn = document.createElement('button');
  nextBtn.textContent = 'Weiter \u2192';
  Object.assign(nextBtn.style, { background: 'rgba(136,204,255,0.12)', color: CLR_PRIMARY, border: '1px solid rgba(136,204,255,0.3)', fontFamily: 'monospace', cursor: 'pointer', fontSize: fontXs(), padding: '6px 14px', borderRadius: radius(), fontWeight: '700', transition: 'background 150ms, border-color 150ms', marginLeft: 'auto' });
  nextBtn.addEventListener('pointerenter', () => { nextBtn.style.background = 'rgba(136,204,255,0.2)'; nextBtn.style.borderColor = 'rgba(136,204,255,0.5)'; }); nextBtn.addEventListener('pointerleave', () => { nextBtn.style.background = 'rgba(136,204,255,0.12)'; nextBtn.style.borderColor = 'rgba(136,204,255,0.3)'; });
  navRow.append(backBtn, nextBtn);
  hubContent.append(step1El, step2El, step3El, navRow);
  root.append(activeContent, inactiveContent, hubContent);

  return { root, tabBtns: { active: tabActive, inactive: tabInactive, hub: tabHub }, activeContent, inactiveContent, hubContent, activeTravelsContainer, inactiveTravelsContainer, stepIndicator, backBtn, nextBtn, step1El, step2El, step3El, startSelect, startLabel, mapStartBtn, endSelect, endLabel, mapEndBtn, sliders, sliderLabels, shipWarpIcons, warpInfoEl, allBtn, noneBtn, modeCards, sendBtn, errorEl, statusEl };
}

function _buildStepIndicator(): HTMLDivElement {
  const si = div({ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px 14px 6px' });
  for (let i = 0; i < 3; i++) {
    const dot = document.createElement('span');
    dot.dataset.step = String(i + 1);
    Object.assign(dot.style, { width: '8px', height: '8px', borderRadius: '50%', background: i === 0 ? CLR_PRIMARY : 'rgba(100,180,255,0.2)', transition: 'background 200ms ease' });
    si.appendChild(dot);
    if (i < 2) { const line = document.createElement('span'); Object.assign(line.style, { width: '24px', height: '1px', background: 'rgba(100,180,255,0.15)' }); si.appendChild(line); }
  }
  return si;
}
